'use client';

import { useEffect, useState } from 'react';
import type { BlogTocHeading } from '@/lib/blog-toc';

interface BlogTableOfContentsProps {
  headings: BlogTocHeading[];
}

function TocLinks({
  activeId,
  headings,
  onLinkClick,
}: BlogTableOfContentsProps & {
  activeId: string;
  onLinkClick: (id: string) => void;
}) {
  return (
    <ol className="blog-toc-list">
      {headings.map((heading) => (
        <li key={heading.id}>
          <a
            className={`blog-toc-link blog-toc-link--level-${heading.level} ${
              activeId === heading.id ? 'blog-toc-link--active' : ''
            }`}
            href={`#${heading.id}`}
            onClick={() => onLinkClick(heading.id)}
          >
            {heading.text}
          </a>
        </li>
      ))}
    </ol>
  );
}

export default function BlogTableOfContents({ headings }: BlogTableOfContentsProps) {
  const [activeId, setActiveId] = useState(headings[0]?.id || '');

  useEffect(() => {
    const headingElements = headings
      .map((heading) => document.getElementById(heading.id))
      .filter((element): element is HTMLElement => Boolean(element));

    if (!headingElements.length) return undefined;

    let ticking = false;

    const updateActiveHeading = () => {
      const offset = 128;
      let currentId = headingElements[0].id;

      for (const element of headingElements) {
        if (element.getBoundingClientRect().top <= offset) {
          currentId = element.id;
        } else {
          break;
        }
      }

      setActiveId(currentId);
    };

    const requestUpdate = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        updateActiveHeading();
      });
    };

    const observer = new IntersectionObserver(requestUpdate, {
      rootMargin: '-96px 0px -65% 0px',
      threshold: [0, 1],
    });

    headingElements.forEach((element) => observer.observe(element));
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    updateActiveHeading();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, [headings]);

  return (
    <div className="blog-toc-shell">
      <nav
        className="blog-toc-desktop"
        aria-labelledby="blog-toc-desktop-title"
        aria-label="Table of contents"
      >
        <p className="blog-toc-title" id="blog-toc-desktop-title">
          Table of contents
        </p>
        <TocLinks activeId={activeId} headings={headings} onLinkClick={setActiveId} />
      </nav>

      <details className="blog-toc-mobile">
        <summary className="blog-toc-summary">Table of contents</summary>
        <TocLinks activeId={activeId} headings={headings} onLinkClick={setActiveId} />
      </details>
    </div>
  );
}
